import React from 'react';
import { MESSAGE_DELETE_FOR_EVERYONE_WINDOW_MS, MESSAGE_EDIT_WINDOW_MS } from '@welynk/types';
import type { Conversation, ConversationMessage } from '@/lib/services/message-api';
import { APPLICATION_STATUS_LABELS } from '@/lib/services/application-api';

/** Messages from one sender this close together share a bubble group. */
const GROUP_GAP_MS = 5 * 60_000;

export const MESSAGE_MAX_LENGTH = 4000;

function startOfDay(d: Date): number {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c.getTime();
}

export function formatClock(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

export function formatFullTimestamp(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function formatDayLabel(iso: string): string {
  const day = startOfDay(new Date(iso));
  const today = startOfDay(new Date());
  if (day === today) return 'Today';
  if (day === today - 86_400_000) return 'Yesterday';
  const d = new Date(iso);
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString('en-US', {
    weekday: day > today - 6 * 86_400_000 ? 'long' : undefined,
    month: 'long',
    day: 'numeric',
    year: sameYear ? undefined : 'numeric',
  });
}

/** Compact inbox time: "10:42 AM", "Yesterday", "Mon", "12 Mar". */
export function formatListTime(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const day = startOfDay(d);
  const today = startOfDay(new Date());
  if (day === today) return formatClock(iso);
  if (day === today - 86_400_000) return 'Yesterday';
  if (day > today - 6 * 86_400_000) return d.toLocaleDateString('en-US', { weekday: 'short' });
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

export type ThreadMessage = ConversationMessage & {
  /** Client-only state for messages still being sent (or that failed). */
  status?: 'sending' | 'failed';
};

export type ThreadItem =
  | { kind: 'day'; key: string; label: string }
  | { kind: 'unread'; key: string }
  | { kind: 'system'; key: string; message: ThreadMessage }
  | { kind: 'group'; key: string; senderId: string; messages: ThreadMessage[] };

/**
 * Day separators, an optional "new messages" line before `firstUnreadId`,
 * and runs of messages from the same sender grouped into one cluster.
 */
export function buildThreadItems(
  messages: ThreadMessage[],
  firstUnreadId: string | null
): ThreadItem[] {
  const items: ThreadItem[] = [];
  let lastDay = '';
  let group: Extract<ThreadItem, { kind: 'group' }> | null = null;

  for (const msg of messages) {
    const day = formatDayLabel(msg.sentAt);
    if (day !== lastDay) {
      items.push({ kind: 'day', key: `day-${msg.id}`, label: day });
      lastDay = day;
      group = null;
    }
    if (msg.id === firstUnreadId) {
      items.push({ kind: 'unread', key: 'unread' });
      group = null;
    }
    if (msg.type === 'SYSTEM' || msg.type === 'AI_NOTIFICATION') {
      items.push({ kind: 'system', key: msg.id, message: msg });
      group = null;
      continue;
    }
    const prev = group?.messages[group.messages.length - 1];
    if (
      group &&
      prev &&
      group.senderId === msg.senderId &&
      new Date(msg.sentAt).getTime() - new Date(prev.sentAt).getTime() < GROUP_GAP_MS
    ) {
      group.messages.push(msg);
    } else {
      group = { kind: 'group', key: `g-${msg.id}`, senderId: msg.senderId, messages: [msg] };
      items.push(group);
    }
  }
  return items;
}

const URL_RE = /(https?:\/\/[^\s<]+[^\s<.,;:!?)\]'"])/g;

/** Message text with http(s) links made clickable (opened in a new tab). */
export function linkify(text: string): React.ReactNode[] {
  return text.split(URL_RE).map((part, i) =>
    i % 2 === 1 ? (
      <a key={i} href={part} target="_blank" rel="noopener noreferrer">
        {part}
      </a>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    )
  );
}

// Inline marks: `code`, *bold*, _italic_ (not inside words, so snake_case and
// 2*3*4 stay as typed), plus links. Rendered as React nodes, never as HTML.
const INLINE_RE =
  /(`[^`\n]+`|(?<![\w*])\*(?=\S)[^*\n]+?(?<=\S)\*(?![\w*])|(?<![\w_])_(?=\S)[^_\n]+?(?<=\S)_(?![\w_])|https?:\/\/[^\s<]+[^\s<.,;:!?)\]'"])/g;

function formatInline(text: string, keyPrefix: string): React.ReactNode[] {
  return text.split(INLINE_RE).map((part, i) => {
    const key = `${keyPrefix}-${i}`;
    if (i % 2 === 0) return <React.Fragment key={key}>{part}</React.Fragment>;
    if (part.startsWith('`')) return <code key={key}>{part.slice(1, -1)}</code>;
    if (part.startsWith('*')) return <strong key={key}>{part.slice(1, -1)}</strong>;
    if (part.startsWith('_')) return <em key={key}>{part.slice(1, -1)}</em>;
    return (
      <a key={key} href={part} target="_blank" rel="noopener noreferrer">
        {part}
      </a>
    );
  });
}

const BULLET_RE = /^\s*[-•]\s+/;

/**
 * Message text with light formatting: *bold*, _italic_, `code`, "- " bullet
 * lists, and clickable links. Line breaks are kept (the bubble is pre-wrap).
 */
export function formatMessageText(text: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  const lines = text.split('\n');
  let i = 0;
  while (i < lines.length) {
    if (BULLET_RE.test(lines[i])) {
      const items: string[] = [];
      while (i < lines.length && BULLET_RE.test(lines[i])) {
        items.push(lines[i].replace(BULLET_RE, ''));
        i++;
      }
      out.push(
        <ul key={`ul-${i}`} className="wl-chat-list-md">
          {items.map((item, j) => (
            <li key={j}>{formatInline(item, `li-${i}-${j}`)}</li>
          ))}
        </ul>
      );
      continue;
    }
    // Plain lines keep their newline; a list already breaks the flow.
    const next = lines[i + 1];
    const newline = i < lines.length - 1 && !(next !== undefined && BULLET_RE.test(next));
    out.push(
      <React.Fragment key={`l-${i}`}>
        {formatInline(lines[i], `l-${i}`)}
        {newline ? '\n' : ''}
      </React.Fragment>
    );
    i++;
  }
  return out;
}

/** One-line description of a message for quotes, pins and the composer banner. */
export function messageSnippet(m: {
  content: string | null;
  deletedAt?: string | null;
  deleted?: boolean;
  attachments?: Array<{ fileName: string }>;
  attachmentName?: string | null;
}): string {
  if (m.deletedAt || m.deleted) return 'Deleted message';
  const file = m.attachments?.[0]?.fileName ?? m.attachmentName ?? null;
  // Plain text: drop formatting marks (*bold*, _italic_, `code`) and collapse lines.
  const text = m.content
    ?.replace(/`([^`\n]+)`/g, '$1')
    .replace(/(?<![\w*])\*(?=\S)([^*\n]+?)(?<=\S)\*(?![\w*])/g, '$1')
    .replace(/(?<![\w_])_(?=\S)([^_\n]+?)(?<=\S)_(?![\w_])/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
  if (text) return text;
  return file ? `📎 ${file}` : 'Message';
}

export function canEditMessage(m: ConversationMessage, userId: string): boolean {
  return (
    m.senderId === userId &&
    !m.deletedAt &&
    (m.type === 'TEXT' || m.type === 'ATTACHMENT') &&
    Date.now() - new Date(m.sentAt).getTime() < MESSAGE_EDIT_WINDOW_MS
  );
}

export function canDeleteForEveryone(m: ConversationMessage, userId: string): boolean {
  return (
    m.senderId === userId &&
    !m.deletedAt &&
    Date.now() - new Date(m.sentAt).getTime() < MESSAGE_DELETE_FOR_EVERYONE_WINDOW_MS
  );
}

/** A link that opens this inbox on the thread, scrolled to the message. */
export function messageLink(conversationId: string, messageId: string): string {
  const url = new URL(window.location.href);
  url.search = '';
  url.hash = '';
  url.searchParams.set('conversationId', conversationId);
  url.searchParams.set('messageId', messageId);
  return url.toString();
}

// ── Drafts: unsent text survives switching threads and reloads (this browser only).

const draftKey = (conversationId: string) => `wl.chat.draft.${conversationId}`;

export function loadDraft(conversationId: string): string {
  try {
    return localStorage.getItem(draftKey(conversationId)) ?? '';
  } catch {
    return '';
  }
}

export function saveDraft(conversationId: string, text: string): void {
  try {
    if (text.trim()) localStorage.setItem(draftKey(conversationId), text);
    else localStorage.removeItem(draftKey(conversationId));
  } catch {
    // storage unavailable (private mode): drafts just don't persist
  }
}

/** One-line inbox preview of the latest message. */
export function previewText(conv: Conversation): string {
  const kind = conv.lastMessageAttachmentKind;
  const fileLabel =
    kind === 'IMAGE' ? '📷 Photo' : kind === 'VIDEO' ? '🎥 Video' : kind ? '📄 Document' : null;
  if (conv.lastMessage) {
    const text = messageSnippet({ content: conv.lastMessage });
    return fileLabel ? `${fileLabel} · ${text}` : text;
  }
  if (fileLabel) return fileLabel;
  if (conv.lastMessageType === 'ATTACHMENT') return 'Shared a file';
  return conv.lastMessageAt ? '' : 'No messages yet';
}

/** "Interview scheduled" for INTERVIEW_SCHEDULED (labels from the applications module). */
export function applicationStatusLabel(status: string | null | undefined): string | null {
  if (!status) return null;
  return (
    (APPLICATION_STATUS_LABELS as Record<string, string>)[status] ??
    status.charAt(0) + status.slice(1).toLowerCase().replace(/_/g, ' ')
  );
}

/** Muted right now (mutes end on their own). */
export function isMuted(conv: Pick<Conversation, 'mutedUntil'>): boolean {
  return !!conv.mutedUntil && new Date(conv.mutedUntil).getTime() > Date.now();
}

/** What a thread shows as unread: its count, or a dot (1) after "Mark as unread". */
export function unreadOf(conv: Pick<Conversation, 'unreadCount' | 'markedUnread'>): number {
  return conv.unreadCount > 0 ? conv.unreadCount : conv.markedUnread ? 1 : 0;
}

/** Unread that counts toward badges: muted threads stay quiet. */
export function badgeUnread(conversations: Conversation[] | undefined): number {
  return (conversations ?? []).reduce((n, c) => n + (isMuted(c) ? 0 : unreadOf(c)), 0);
}

/** "Muted for 8 hours" style label for a mute end. */
export function muteLabel(until: string): string {
  const end = new Date(until);
  if (end.getFullYear() >= 9999) return 'Muted until you unmute';
  return `Muted until ${end.toLocaleString('en-US', {
    weekday: 'short',
    hour: 'numeric',
    minute: '2-digit',
  })}`;
}
